import React from 'react';
import {
  Rocket, BarChart3, Users, Wallet, ArrowRight, Sparkles,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * EcosystemHowItWorks
 * ------------------
 * Public-facing visual explainer used on both `/` (Home, post-launch)
 * and `/` (ComingSoon, pre-launch). Structurally inspired by JVZoo's
 * 4-step section, adapted to theailedger's voice and visual system —
 * cyan/dark, premium, no fluff numbers (deliberately avoids hard-coded
 * partner counts so the copy stays accurate as the marketplace grows).
 *
 * Position: directly under <AllFeaturedPartnersStrip /> (the moving
 * affiliate-logos ticker), directly above the creator marketplace tabs.
 *
 * Why a dedicated component (vs inline JSX)?
 *   - Used on two pages — DRY.
 *   - The hook copy needs to read identically on Home + ComingSoon so
 *     creators get the same first impression whether they arrive pre-
 *     or post-launch.
 *   - Keeps Home.js / ComingSoon.js readable; both are already large.
 */
const STEPS = [
  {
    n: 1,
    icon: Rocket,
    title: 'List your AI tool in minutes',
    sub: 'No dev work. No 12-step onboarding. Drop in your product page, connect Stripe, and you’re live.',
  },
  {
    n: 2,
    icon: BarChart3,
    title: 'Track every click, sale & affiliate in one dashboard',
    sub: 'Real-time analytics built for creators, not analysts. Mobile-friendly, exportable, and dead simple.',
  },
  {
    n: 3,
    icon: Users,
    title: 'Plug into a built-in affiliate network',
    sub: 'Every partner on theailedger is vetted and ready to start promoting on day one — no recruiting, no cold outreach.',
  },
  {
    n: 4,
    icon: Wallet,
    title: 'Get paid faster than your competitors',
    sub: 'Stripe Connect + Global Payouts handle the rest. No spreadsheets, no chasing commissions, no late checks.',
  },
];

export default function EcosystemHowItWorks({ ctaLabel, onCtaClick, hideCta = false }) {
  const navigate = useNavigate();
  const handleCta = onCtaClick || (() => navigate('/submit'));

  return (
    <section
      className="relative py-16 md:py-20"
      data-testid="ecosystem-how-it-works"
      aria-labelledby="ecosystem-hook-heading"
    >
      {/* Visual separator: a single thin top divider replaces the previous
          radial glow. The glow was concentrated behind step 3 on narrower
          windows and read as a "spotlight strobe" washing out the card —
          flagged by the promoter on 2026-05-21. A flat divider keeps the
          section anchored without the lighting artifact. */}
      <div
        aria-hidden
        className="absolute top-0 left-1/2 -translate-x-1/2 h-px w-40 bg-gradient-to-r from-transparent via-white/15 to-transparent"
      />

      <div className="relative container mx-auto px-4 md:px-12 max-w-6xl">
        {/* Hook headline ------------------------------------------------ */}
        <div className="text-center mb-12 md:mb-14">
          <div className="inline-flex items-center gap-1.5 mb-4 px-3 py-1 rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/[0.06]">
            <Sparkles className="h-3.5 w-3.5 text-[#00F0FF]" />
            <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#00F0FF]">
              How it works
            </span>
          </div>
          <h2
            id="ecosystem-hook-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight leading-[1.1] max-w-4xl mx-auto"
            data-testid="ecosystem-hook-heading"
          >
            We&apos;re not just another marketplace.
            <br className="hidden sm:block" />{' '}
            <span className="text-[#00F0FF]">
              We&apos;re a full sales ecosystem built for AI creators to scale.
            </span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-white/60 max-w-2xl mx-auto leading-relaxed">
            Other platforms hand you a checkout link and disappear.
            We handle the whole sales engine — affiliates, payouts, analytics,
            trust — so you can focus on building.
          </p>
        </div>

        {/* 4-step grid ------------------------------------------------- */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <article
                key={s.n}
                className="relative rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:bg-white/[0.05] hover:border-[#00F0FF]/30 transition-all duration-200"
                data-testid={`ecosystem-step-${s.n}`}
              >
                {/* Big translucent step number behind the icon. Visual
                    rhythm cue — your eye reads 1→2→3→4 across the row. */}
                <div
                  aria-hidden
                  className="absolute top-4 right-5 text-6xl font-black text-white/[0.04] leading-none select-none"
                >
                  {s.n}
                </div>

                <div className="relative">
                  <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#00F0FF]/10 border border-[#00F0FF]/25 mb-4">
                    <Icon className="h-5 w-5 text-[#00F0FF]" strokeWidth={2.25} />
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#00F0FF]/80 mb-1.5">
                    Step {s.n}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 leading-snug">
                    {s.title}
                  </h3>
                  <p className="text-sm text-white/60 leading-relaxed">
                    {s.sub}
                  </p>
                </div>
              </article>
            );
          })}
        </div>

        {/* CTA strip — hidden on pages where another primary CTA
            already sits directly below this section (e.g. Coming Soon's
            "Start Selling Your AI" card), to avoid duplicate creator
            calls-to-action competing for the same click. */}
        {!hideCta && (
          <div className="mt-10 md:mt-12 text-center">
            <button
              type="button"
              onClick={handleCta}
              data-testid="ecosystem-cta-btn"
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#00F0FF] text-black font-bold text-sm sm:text-base hover:bg-[#00F0FF]/90 transition shadow-[0_0_30px_rgba(0,240,255,0.25)]"
            >
              {ctaLabel || 'Start selling on theailedger'}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <p className="mt-3 text-xs text-white/50">
              No upfront cost. 5% platform fee. You only pay when you make a sale.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
